import { ArrowLeft, Camera, QrCode, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { apiRequest } from '../api/client'
import type { Equipment } from '../api/types'
import { Button } from '../components/ui'
import { errorMessage } from '../utils/format'

type Detector = { detect(source: HTMLVideoElement): Promise<Array<{ rawValue?: string }>> }
type DetectorConstructor = new (options: { formats: string[] }) => Detector

/** Открывает карточку оборудования по QR-этикетке (ссылка /equipment/{id}) или по коду, введённому вручную. */
export function ScanPage() {
  const navigate = useNavigate()
  const video = useRef<HTMLVideoElement>(null)
  const [code, setCode] = useState('')
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraState, setCameraState] = useState<'idle' | 'starting' | 'active'>('idle')
  const [error, setError] = useState('')
  const [searching, setSearching] = useState(false)

  const closeCamera = useCallback(() => { setCameraOpen(false); setCameraState('idle') }, [])

  const openValue = useCallback(async (value: string): Promise<boolean> => {
    const raw = value.trim()
    if (!raw) return false
    try {
      const parsed = new URL(raw, window.location.origin)
      const match = parsed.pathname.match(/^\/equipment\/([0-9a-fA-F-]{36})\/?$/)
      if (match?.[1]) { navigate(`/equipment/${match[1]}`); return true }
    } catch { /* ручной код — не ссылка */ }
    if (/^[0-9a-fA-F-]{36}$/.test(raw)) { navigate(`/equipment/${raw}`); return true }
    setSearching(true)
    try {
      const found = await apiRequest<unknown>(`/equipment?q=${encodeURIComponent(raw)}&limit=5`, { rawResponse: true }) as { data?: Equipment[] }
      const items = found.data || []
      const exact = items.find((item) => (item.externalKey || '').toLocaleLowerCase('ru') === raw.toLocaleLowerCase('ru')) || (items.length === 1 ? items[0] : undefined)
      if (exact) { navigate(`/equipment/${exact.id}`); return true }
      if (items.length > 1) { navigate(`/equipment?q=${encodeURIComponent(raw)}`); return true }
      return false
    } catch (caught) {
      setError(errorMessage(caught))
      return false
    } finally {
      setSearching(false)
    }
  }, [navigate])

  useEffect(() => {
    if (!cameraOpen) return
    let stream: MediaStream | null = null
    let stopped = false
    let timer: number | undefined
    const stop = () => {
      if (timer) { window.clearInterval(timer); timer = undefined }
      stream?.getTracks().forEach((track) => track.stop())
      stream = null
      if (video.current) { if (!video.current.paused) video.current.pause(); video.current.srcObject = null }
    }
    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('Камера недоступна на этом устройстве.')
        const DetectorClass = (window as Window & { BarcodeDetector?: DetectorConstructor }).BarcodeDetector
        if (!DetectorClass) throw new Error('Сканирование камерой не поддерживается в этом браузере. Введите код оборудования вручную.')
        const requested = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
        if (stopped) { requested.getTracks().forEach((track) => track.stop()); return }
        stream = requested
        if (!video.current) throw new Error('Не удалось открыть изображение с камеры.')
        video.current.srcObject = stream
        await video.current.play()
        if (stopped) { stop(); return }
        setCameraState('active')
        const detector = new DetectorClass({ formats: ['qr_code'] })
        timer = window.setInterval(() => {
          if (stopped || !video.current) return
          void detector.detect(video.current).then(async (codes) => {
            const value = codes[0]?.rawValue
            if (!value || stopped) return
            if (await openValue(value)) closeCamera()
            else setError('Этот QR-код не относится к оборудованию. Попробуйте другую этикетку или введите код вручную.')
          }).catch(() => { if (!stopped) setError('Не удалось распознать QR-код. Держите этикетку в кадре или введите код вручную.') })
        }, 450)
      } catch (caught) {
        stop()
        if (stopped) return
        setError(caught instanceof Error ? caught.message : 'Не удалось подключить камеру.')
        setCameraState('idle')
        setCameraOpen(false)
      }
    }
    void start()
    return () => { stopped = true; stop() }
  }, [cameraOpen, closeCamera, openValue])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    if (!(await openValue(code))) setError('Оборудование с таким кодом не найдено. Проверьте надпись на этикетке.')
  }

  return <div className="scan-page">
    <Link className="back-link" to="/work-orders"><ArrowLeft size={17} />К заданиям</Link>
    <div className="scan-card">
      <span className="scan-card__icon"><QrCode size={36} /></span>
      <h2>Открыть оборудование по QR</h2>
      <p>Наведите камеру на этикетку или введите код оборудования вручную. Откроется карточка оборудования с его работами и дефектами.</p>
      {cameraOpen && <div className="camera-panel" aria-busy={cameraState === 'starting' || undefined}><video ref={video} muted playsInline aria-label="Изображение с камеры" />{cameraState === 'starting' ? <p role="status">Включаем камеру…</p> : <p role="status">Камера включена. Наведите её на QR-код.</p>}<Button variant="quiet" onClick={closeCamera}><X size={16} />Закрыть камеру</Button></div>}
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="scan-actions"><Button variant="secondary" loading={cameraState === 'starting'} disabled={cameraOpen} onClick={() => { setError(''); setCameraState('starting'); setCameraOpen(true) }}>{cameraState !== 'starting' && <Camera size={17} />}{cameraState === 'starting' ? 'Включаем камеру' : 'Сканировать камерой'}</Button></div>
      <form onSubmit={(event) => void submit(event)}>
        <label className="field"><span>Код оборудования</span><input name="equipmentCode" value={code} onChange={(event) => { setCode(event.target.value); if (error) setError('') }} placeholder="Например, ВЕН-01" autoCapitalize="characters" /></label>
        <Button type="submit" loading={searching} disabled={!code.trim()}>Открыть карточку</Button>
      </form>
    </div>
  </div>
}
